import { getCurrentScope, onScopeDispose, type Ref } from 'vue'

import { useReducedMotion } from './useReducedMotion'

/** The easing tokens a sequence may use, named after their `--ease-*` token. */
type MotionEasing = 'soft' | 'soft-exit' | 'standard' | 'spring' | 'spring-bouncy'

/** When and how one part of a sequence plays. */
interface MotionTiming {
  /** Length in milliseconds. */
  readonly duration: number
  /** Wait before it starts, in milliseconds. */
  readonly delay?: number
  /** The easing token it runs on. */
  readonly easing: MotionEasing
  /** `backwards` (the default) holds the first keyframe during the wait; `none` shows the resting style; `both` also holds the last keyframe after the end, for an element that leaves the page once it has played. */
  readonly fill?: 'backwards' | 'none' | 'both'
}

/** Plays timed parts of one moment with the Web Animations API. */
export interface MotionSequence {
  /** True when the founder asked for reduced motion. */
  readonly reduced: Readonly<Ref<boolean>>
  /** Plays one part and returns it, or `null` for a missing element or where the API is missing. */
  play(
    element: Element | null | undefined,
    keyframes: Keyframe[],
    timing: MotionTiming,
  ): Animation | null
  /** Stops every part still playing, leaving each element on its resting style. */
  cancel(): void
  /** Reads a CSS custom property of the document, such as a spacing token. */
  token(name: `--${string}`): string
}

const EASING_FALLBACK = 'ease-out'

function readToken(name: string): string {
  if (typeof document === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/** Plays the parts of a sequenced moment; every part ends back on the element's own resting style unless it holds its last keyframe. */
export function useMotionSequence(): MotionSequence {
  const reduced = useReducedMotion()
  const running = new Set<Animation>()
  const easings = new Map<MotionEasing, string>()

  function easing(name: MotionEasing): string {
    const cached = easings.get(name)
    if (cached !== undefined) return cached
    const read = readToken(`--ease-${name}`) || EASING_FALLBACK
    easings.set(name, read)
    return read
  }

  function play(
    element: Element | null | undefined,
    keyframes: Keyframe[],
    timing: MotionTiming,
  ): Animation | null {
    if (!element || typeof element.animate !== 'function') return null
    const animation = element.animate(keyframes, {
      duration: timing.duration,
      delay: timing.delay ?? 0,
      easing: easing(timing.easing),
      fill: timing.fill ?? 'backwards',
    })
    running.add(animation)
    const forget = (): void => {
      running.delete(animation)
    }
    animation.addEventListener('finish', forget)
    animation.addEventListener('cancel', forget)
    return animation
  }

  function cancel(): void {
    for (const animation of running) animation.cancel()
    running.clear()
  }

  if (getCurrentScope()) onScopeDispose(cancel)

  return { reduced, play, cancel, token: readToken }
}
